import { useQuery } from '@tanstack/react-query';
import {
  CalendarRange,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
  LayoutGrid,
  Pencil,
  Plus,
  Trash2,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelectedClub } from '@/features/club';
import {
  blocksOnDay,
  buildNameMap,
  collectPlayerIds,
  competitionColorIndex,
  DraggableCard,
  findSlotConflict,
  fromMinutes,
  groupPoolByCompetition,
  hhmm,
  magnetStarts,
  matchCardDensity,
  MatchScoreRow,
  matchesOnCourt,
  matchPlayerIds,
  plannerHours,
  roundLabel,
  slotBar,
  slotHeightRem,
  snapDropToTime,
  toBracketMatch,
  todayIso,
  toMinutes,
  useAssignCourtSlotMutation,
  useClearCourtSlotMutation,
  useDeleteCourtMutation,
  useMatchPlannerQuery,
  useSchedulePublicationQuery,
  useSetSchedulePublicationMutation,
  type ApiClubCourt,
  type ApiMatchPlanner,
  type ApiPlannerMatch,
  type SlotConflict,
} from '@/features/competition';
import { getUsersByIds, userKeys } from '@/features/user';
import { ApiError } from '@/shared/api/ApiError';
import { cn } from '@/shared/lib/cn';
import {
  Alert,
  Button,
  Card,
  CardHeader,
  DateInput,
  EmptyState,
  IconButton,
  PageHeader,
  Skeleton,
  useToast,
} from '@/shared/ui';
import { CourtFormDialog } from './infrastructure/CourtFormDialog';

/** Wysokość jednej godziny na osi kalendarza. */
const HOUR_ROW_REM = 3.5;

/** Szerokość kolumny z podpisami godzin. */
const HOUR_COLUMN_REM = 3.5;

/** Minimalna szerokość kolumny kortu — klub bywa dziewięciokortowy. */
const COURT_COLUMN_MIN_REM = 10;

/**
 * Kolory rozgrywek na kalendarzu — indeks z `competitionColorIndex`.
 * Paleta celowo krótka: przy więcej niż sześciu aktywnych turniejach kolory
 * zaczynają się powtarzać, ale etykieta turnieju zawsze jest na karcie.
 */
const COMPETITION_COLORS = [
  { dot: 'bg-brand-500', card: 'border-brand-200 bg-brand-subtle' },
  { dot: 'bg-info-500', card: 'border-info-100 bg-info-50' },
  { dot: 'bg-success-500', card: 'border-success-100 bg-success-50' },
  { dot: 'bg-warning-500', card: 'border-warning-100 bg-warning-50' },
  { dot: 'bg-danger-500', card: 'border-danger-100 bg-danger-50' },
  { dot: 'bg-border-strong', card: 'border-border bg-surface-sunken' },
] as const;

/** Przesuwa datę ISO o podaną liczbę dni. */
function shiftDay(isoDate: string, days: number): string {
  const date = new Date(`${isoDate}T00:00:00`);
  date.setDate(date.getDate() + days);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/** Nagłówek dnia: „niedziela, 24.08.2026”. */
function dayLabel(isoDate: string): string {
  return new Intl.DateTimeFormat('pl-PL', {
    weekday: 'long',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(`${isoDate}T00:00:00`));
}

/** Stan okna kortu: tworzenie nowego albo edycja istniejącego. */
type CourtDialogState = { mode: 'create' } | { mode: 'edit'; court: ApiClubCourt } | null;

/**
 * Ekran „Korty i kalendarz meczów" — jedna strona łącząca zarządzanie kortami
 * klubu z kalendarzem planowania: kolumny to korty, wiersze to godziny dnia,
 * a mecze aktywnych turniejów przeciąga się z puli na sloty (i między nimi).
 *
 * Mecze rozstrzygnięte (wynik, walkower) znikają z kalendarza — backend ich nie
 * zwraca i zwalniają miejsce na kolejne mecze.
 */
export function InfrastructurePage() {
  const { t } = useTranslation('tournament');
  const { showToast } = useToast();
  const { clubId, isEmpty: hasNoClubs } = useSelectedClub();

  const [date, setDate] = useState(todayIso());
  const [dialog, setDialog] = useState<CourtDialogState>(null);
  /** Mecz aktualnie przeciągany — od niego zależy wysokość cienia na kalendarzu. */
  const [draggingId, setDraggingId] = useState<string | null>(null);
  /** Slot pod kursorem przeciągania: kort + przyciągnięta godzina startu. */
  const [hoverSlot, setHoverSlot] = useState<{ courtId: string; time: string } | null>(null);
  /**
   * Rozwinięte turnieje w puli — trzymamy tylko rozwinięte, bo domyślnie
   * wszystkie sekcje są zwinięte i lista pokazuje same nagłówki turniejów.
   */
  const [openSections, setOpenSections] = useState<ReadonlySet<string>>(new Set());

  function toggleSection(competitionId: string) {
    setOpenSections((current) => {
      const next = new Set(current);
      if (!next.delete(competitionId)) next.add(competitionId);
      return next;
    });
  }

  const plannerQuery = useMatchPlannerQuery(clubId, date);
  const planner = plannerQuery.data;

  /**
   * Widoczność planu meczów na stronie prezentacyjnej. Kalendarz powstaje na
   * długo przed imprezą i bywa przestawiany do ostatniej chwili, więc plan
   * trafia do widzów dopiero wtedy, gdy organizator sam go upubliczni.
   */
  const publicationQuery = useSchedulePublicationQuery(clubId);
  const publicationMutation = useSetSchedulePublicationMutation(clubId ?? '');
  const schedulePublished = publicationQuery.data?.published ?? false;

  const assignMutation = useAssignCourtSlotMutation();
  const clearMutation = useClearCourtSlotMutation();
  const deleteCourtMutation = useDeleteCourtMutation(clubId ?? '');

  const allEntries = useMemo(
    () => [...(planner?.scheduledMatches ?? []), ...(planner?.unscheduledMatches ?? [])],
    [planner],
  );
  const entryById = useMemo(
    () => new Map(allEntries.map((entry) => [entry.match.id, entry])),
    [allEntries],
  );

  const playerIds = useMemo(
    () => collectPlayerIds(allEntries.map((entry) => entry.match)).sort(),
    [allEntries],
  );
  const profilesQuery = useQuery({
    queryKey: [...userKeys.all, 'batch', playerIds.join(',')],
    queryFn: () => getUsersByIds(playerIds),
    enabled: playerIds.length > 0,
  });
  const names = useMemo(() => buildNameMap(profilesQuery.data ?? []), [profilesQuery.data]);

  const competitionIds = useMemo(
    () => (planner?.competitions ?? []).map((competition) => competition.id),
    [planner],
  );
  const poolSections = useMemo(
    () => groupPoolByCompetition(planner?.unscheduledMatches ?? []),
    [planner],
  );

  function describe(error: unknown, fallback: string): string {
    return error instanceof ApiError ? error.message : fallback;
  }

  function colorOf(competitionId: string | null) {
    return COMPETITION_COLORS[
      competitionColorIndex(competitionIds, competitionId) % COMPETITION_COLORS.length
    ]!;
  }

  /**
   * Szuka kolizji proponowanego slotu — używane i przy podglądzie przeciągania,
   * i przy samym upuszczeniu, żeby cień i blokada mówiły dokładnie to samo.
   */
  function conflictFor(
    court: ApiClubCourt,
    entry: ApiPlannerMatch,
    startTime: string,
  ): SlotConflict | null {
    if (!planner) return null;
    return findSlotConflict(planner.scheduledMatches, court, date, startTime, {
      durationMinutes: entry.durationMinutes,
      matchId: entry.match.id,
      playerIds: matchPlayerIds(entry),
    });
  }

  /** Zamienia kolizję na komunikat dla organizatora. */
  function conflictMessage(conflict: SlotConflict): string {
    if (conflict.kind === 'match') {
      return t('infrastructure.calendar.conflictMatch', {
        from: conflict.startTime,
        to: conflict.endTime,
        name: conflict.label ?? '',
      });
    }
    if (conflict.kind === 'player') {
      return t('infrastructure.calendar.conflictPlayer', {
        player: (conflict.playerId ? names.get(conflict.playerId) : null) ?? t('infrastructure.calendar.somePlayer'),
        from: conflict.startTime,
        to: conflict.endTime,
        name: conflict.label ?? '',
      });
    }
    return t('infrastructure.calendar.conflictBlock', {
      from: conflict.startTime,
      to: conflict.endTime,
    });
  }

  /** Upuszczenie meczu na kolumnę kortu — przypisanie slotu (kort, dzień, godzina). */
  function handleDropOnCourt(court: ApiClubCourt, matchId: string, startTime: string) {
    const entry = entryById.get(matchId);
    if (!entry || !planner) return;

    // Kolizję pokazujemy od razu, zanim poleci zapis — kort i przerwę techniczną
    // backend i tak zweryfikuje (409), kolizji zawodnika pilnujemy tutaj.
    const conflict = conflictFor(court, entry, startTime);
    if (conflict) {
      showToast({ tone: 'warning', description: conflictMessage(conflict) });
      return;
    }

    assignMutation.mutate(
      { matchId, payload: { courtId: court.id, date, startTime } },
      {
        onSuccess: () =>
          showToast({
            tone: 'success',
            description: t('infrastructure.toast.slotAssigned', { time: startTime }),
          }),
        onError: (error) =>
          showToast({
            tone: 'danger',
            description: describe(error, t('infrastructure.toast.slotFailed')),
          }),
      },
    );
  }

  /** Upuszczenie na pulę — zdjęcie meczu z kalendarza. */
  function handleDropOnPool(matchId: string) {
    const entry = entryById.get(matchId);
    if (!entry || entry.match.courtId === null) return;
    clearMutation.mutate(matchId, {
      onSuccess: () =>
        showToast({ tone: 'success', description: t('infrastructure.toast.slotCleared') }),
      onError: (error) =>
        showToast({
          tone: 'danger',
          description: describe(error, t('infrastructure.toast.slotFailed')),
        }),
    });
  }

  function handleDeleteCourt(court: ApiClubCourt) {
    if (!window.confirm(t('infrastructure.court.deleteConfirm', { name: court.name }))) return;
    deleteCourtMutation.mutate(court.id, {
      onSuccess: () =>
        showToast({ tone: 'success', description: t('infrastructure.toast.courtDeleted') }),
      onError: (error) =>
        showToast({
          tone: 'danger',
          description: describe(error, t('infrastructure.toast.deleteFailed')),
        }),
    });
  }

  /** Przełącza widoczność planu meczów na stronie prezentacyjnej. */
  function toggleSchedulePublication() {
    const published = !schedulePublished;
    publicationMutation.mutate(
      { published },
      {
        onSuccess: () =>
          showToast({
            tone: 'success',
            description: published
              ? t('infrastructure.publication.publishedToast')
              : t('infrastructure.publication.hiddenToast'),
          }),
        onError: (error) =>
          showToast({
            tone: 'danger',
            description: describe(error, t('infrastructure.publication.failed')),
          }),
      },
    );
  }

  if (hasNoClubs) {
    return (
      <>
        <PageHeader
          title={t('infrastructure.title')}
          description={t('infrastructure.description')}
        />
        <EmptyState
          icon={<LayoutGrid />}
          title={t('infrastructure.noClubTitle')}
          description={t('infrastructure.noClubDescription')}
          className="min-h-[24rem]"
        />
      </>
    );
  }

  const courts = planner?.courts ?? [];
  const draggingEntry = draggingId ? entryById.get(draggingId) : undefined;

  // Kolizja slotu pod kursorem — od niej zależy kolor cienia w kalendarzu.
  // Liczymy ją przy każdym renderze przeciągania: to przebiegnięcie po meczach
  // jednego dnia, więc taniej niż pilnowanie zależności memoizacji.
  const hoverCourt = hoverSlot ? courts.find((court) => court.id === hoverSlot.courtId) : undefined;
  const hoverConflict =
    hoverSlot && hoverCourt && draggingEntry
      ? conflictFor(hoverCourt, draggingEntry, hoverSlot.time)
      : null;

  /** Jednolinijkowy powód czerwonego cienia — pełne zdanie idzie w toaście. */
  function conflictNote(conflict: SlotConflict): string {
    if (conflict.kind === 'player') {
      return t('infrastructure.calendar.notePlayer', {
        player:
          (conflict.playerId ? names.get(conflict.playerId) : null) ??
          t('infrastructure.calendar.somePlayer'),
      });
    }
    return conflict.kind === 'match'
      ? t('infrastructure.calendar.noteCourt')
      : t('infrastructure.calendar.noteBlock');
  }

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t('infrastructure.title')}
        description={t('infrastructure.description')}
        actions={
          <>
            <Button
              variant="secondary"
              leadingIcon={
                schedulePublished ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />
              }
              loading={publicationMutation.isPending}
              disabled={publicationQuery.isPending}
              onClick={toggleSchedulePublication}
            >
              {schedulePublished
                ? t('infrastructure.publication.hide')
                : t('infrastructure.publication.publish')}
            </Button>
            <Button
              leadingIcon={<Plus aria-hidden="true" />}
              onClick={() => setDialog({ mode: 'create' })}
            >
              {t('infrastructure.addCourt')}
            </Button>
          </>
        }
      />

      {/* Pasek dnia — listę aktywnych turniejów niesie pula „Do rozplanowania”. */}
      <div className="flex items-center gap-2">
        <IconButton
          label="Poprzedni dzień"
          icon={<ChevronLeft />}
          variant="secondary"
          onClick={() => setDate(shiftDay(date, -1))}
        />
        <DateInput
          inputSize="sm"
          aria-label={t('infrastructure.calendar.hour')}
          value={date}
          onChange={(event) => setDate(event.target.value)}
          className="w-44"
        />
        <IconButton
          label="Następny dzień"
          icon={<ChevronRight />}
          variant="secondary"
          onClick={() => setDate(shiftDay(date, 1))}
        />
        <span className="text-fg-muted ml-2 hidden text-sm sm:inline">{dayLabel(date)}</span>
        {/* Sama etykieta przycisku nie mówi, jaki jest stan teraz — nazywamy go wprost. */}
        <span
          className={cn(
            'ml-auto flex items-center gap-1.5 text-xs font-semibold',
            schedulePublished ? 'text-success-600' : 'text-fg-muted',
          )}
        >
          {schedulePublished ? (
            <Eye aria-hidden="true" className="size-3.5" />
          ) : (
            <EyeOff aria-hidden="true" className="size-3.5" />
          )}
          {schedulePublished
            ? t('infrastructure.publication.statusPublished')
            : t('infrastructure.publication.statusHidden')}
        </span>
      </div>

      {plannerQuery.isError && <Alert tone="danger">{t('infrastructure.loadError')}</Alert>}

      {plannerQuery.isPending ? (
        <Skeleton className="h-96 w-full" />
      ) : (
        <div className="grid items-start gap-5 xl:grid-cols-[20rem_minmax(0,1fr)]">
          {/* Lewa kolumna — pula meczów do rozplanowania (i strefa zdejmowania) */}
          <Card
            padding="md"
            className={cn(
              'flex flex-col gap-3 transition-colors',
              draggingEntry?.match.courtId && 'border-brand-500 border-dashed',
            )}
            onDragOver={(event: DragEvent<HTMLDivElement>) => event.preventDefault()}
            onDrop={(event: DragEvent<HTMLDivElement>) => {
              event.preventDefault();
              setHoverSlot(null);
              handleDropOnPool(event.dataTransfer.getData('text/plain'));
            }}
          >
            <CardHeader
              title={t('infrastructure.pool.title', {
                count: planner?.unscheduledMatches.length ?? 0,
              })}
              className="mb-0"
            />
            {/* Podpowiedź tylko w trakcie przeciągania meczu z kalendarza —
                poza tym pula ma zostać samą listą turniejów. */}
            {draggingEntry?.match.courtId && (
              <p className="text-fg-subtle text-xs">{t('infrastructure.pool.dropTitle')}</p>
            )}

            {allEntries.length === 0 ? (
              <p className="text-fg-muted text-sm">{t('infrastructure.pool.noMatches')}</p>
            ) : poolSections.length === 0 ? (
              <p className="text-fg-muted text-sm">{t('infrastructure.pool.empty')}</p>
            ) : (
              poolSections.map((section) => {
                const isOpen = openSections.has(section.competitionId);
                return (
                  <div key={section.competitionId} className="flex flex-col gap-2">
                    <button
                      type="button"
                      aria-expanded={isOpen}
                      onClick={() => toggleSection(section.competitionId)}
                      className="text-fg-muted hover:text-fg flex items-center gap-1.5 text-left text-[0.6875rem] font-bold tracking-[0.08em] uppercase transition-colors"
                    >
                      <ChevronDown
                        aria-hidden="true"
                        className={cn(
                          'size-3.5 shrink-0 transition-transform',
                          !isOpen && '-rotate-90',
                        )}
                      />
                      <span
                        className={cn(
                          'size-2 shrink-0 rounded-full',
                          colorOf(section.competitionId).dot,
                        )}
                      />
                      <span className="truncate">{section.competitionName}</span>
                      <span className="text-fg-subtle ml-auto font-normal normal-case tabular-nums">
                        {section.matches.length}
                      </span>
                    </button>
                    {isOpen &&
                      section.matches.map((entry) => (
                        <div
                          key={entry.match.id}
                          onDragStart={() => setDraggingId(entry.match.id)}
                          onDragEnd={() => {
                            setDraggingId(null);
                            setHoverSlot(null);
                          }}
                        >
                          <DraggableCard payload={entry.match.id}>
                            <p className="text-fg-subtle text-xs">
                              {[
                                roundLabel(entry.match.round),
                                entry.match.matchNumber !== null
                                  ? t('infrastructure.pool.matchNumber', {
                                      number: entry.match.matchNumber,
                                    })
                                  : null,
                                t('infrastructure.legend.duration', {
                                  minutes: entry.durationMinutes,
                                }),
                              ]
                                .filter(Boolean)
                                .join(' • ')}
                            </p>
                            <MatchScoreRow
                              match={toBracketMatch(entry.match, names)}
                              showScore={false}
                            />
                          </DraggableCard>
                        </div>
                      ))}
                  </div>
                );
              })
            )}
          </Card>

          {/* Prawa kolumna — kalendarz kortów */}
          {courts.length === 0 ? (
            <EmptyState
              icon={<CalendarRange />}
              title={t('infrastructure.noCourtsTitle')}
              description={t('infrastructure.noCourtsDescription')}
              action={
                <Button
                  leadingIcon={<Plus aria-hidden="true" />}
                  onClick={() => setDialog({ mode: 'create' })}
                >
                  {t('infrastructure.addCourt')}
                </Button>
              }
              className="min-h-[24rem]"
            />
          ) : (
            <Card padding="md" className="min-w-0">
              <PlannerCalendar
                planner={planner!}
                date={date}
                names={names}
                colorOf={colorOf}
                draggingEntry={draggingEntry}
                hoverSlot={hoverSlot}
                hoverConflict={hoverConflict}
                hoverConflictNote={hoverConflict ? conflictNote(hoverConflict) : null}
                onHoverSlot={setHoverSlot}
                onDropOnCourt={handleDropOnCourt}
                onDragStartMatch={setDraggingId}
                onDragEndMatch={() => {
                  setDraggingId(null);
                  setHoverSlot(null);
                }}
                onClearSlot={handleDropOnPool}
                onEditCourt={(court) => setDialog({ mode: 'edit', court })}
                onDeleteCourt={handleDeleteCourt}
              />
            </Card>
          )}
        </div>
      )}

      {dialog && clubId && (
        // Klucz wymusza świeży stan formularza przy każdym otwarciu.
        <CourtFormDialog
          key={dialog.mode === 'edit' ? dialog.court.id : 'create'}
          open
          onClose={() => setDialog(null)}
          clubId={clubId}
          courts={courts}
          {...(dialog.mode === 'edit' ? { court: dialog.court } : {})}
        />
      )}
    </div>
  );
}

/**
 * Siatka kalendarza: kolumny to korty, wiersze to godziny. Mecze leżą jako
 * bloki o wysokości szacowanego czasu meczu; upuszczenie przyciąga się do
 * kwadransa, a cień pokazuje docelowy slot jeszcze przed puszczeniem.
 */
function PlannerCalendar({
  planner,
  date,
  names,
  colorOf,
  draggingEntry,
  hoverSlot,
  hoverConflict,
  hoverConflictNote,
  onHoverSlot,
  onDropOnCourt,
  onDragStartMatch,
  onDragEndMatch,
  onClearSlot,
  onEditCourt,
  onDeleteCourt,
}: {
  planner: ApiMatchPlanner;
  date: string;
  names: ReadonlyMap<string, string>;
  colorOf: (competitionId: string | null) => (typeof COMPETITION_COLORS)[number];
  draggingEntry: ApiPlannerMatch | undefined;
  hoverSlot: { courtId: string; time: string } | null;
  /** Kolizja slotu pod kursorem albo `null`, gdy slot jest wolny. */
  hoverConflict: SlotConflict | null;
  /** Krótki powód kolizji wypisywany na cieniu. */
  hoverConflictNote: string | null;
  onHoverSlot: (slot: { courtId: string; time: string } | null) => void;
  onDropOnCourt: (court: ApiClubCourt, matchId: string, startTime: string) => void;
  onDragStartMatch: (matchId: string) => void;
  onDragEndMatch: () => void;
  onClearSlot: (matchId: string) => void;
  onEditCourt: (court: ApiClubCourt) => void;
  onDeleteCourt: (court: ApiClubCourt) => void;
}) {
  const { t } = useTranslation('tournament');
  const hours = plannerHours();
  const spans = Math.max(1, hours.length - 1);
  const bodyHeight = spans * HOUR_ROW_REM;
  const courts = planner.courts;

  /**
   * Poziomy pasek przewijania wyniesiony NAD siatkę.
   *
   * Kalendarz ma kilkadziesiąt rem wysokości, więc natywny pasek na dole jest
   * poza ekranem — żeby przesunąć kolumny w bok, trzeba było najpierw przewinąć
   * całą stronę. Popularna sztuczka z `transform: rotateX(180deg)` odpada:
   * odwraca układ współrzędnych, a od `clientY - rect.top` zależy wyliczanie
   * godziny przy upuszczaniu meczu. Zamiast tego trzymamy osobną szynę i
   * synchronizujemy `scrollLeft` w obie strony.
   */
  const bodyRef = useRef<HTMLDivElement>(null);
  const railRef = useRef<HTMLDivElement>(null);
  const [contentWidth, setContentWidth] = useState(0);
  const [viewportWidth, setViewportWidth] = useState(0);

  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return;

    const measure = () => {
      setContentWidth(body.scrollWidth);
      setViewportWidth(body.clientWidth);
    };
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(body);
    // Siatka rośnie wraz z liczbą kortów, a nie z rozmiarem kontenera —
    // obserwujemy ją osobno, inaczej szyna zostałaby przy starej szerokości.
    if (body.firstElementChild) observer.observe(body.firstElementChild);
    return () => observer.disconnect();
  }, [courts.length]);

  /** Czy jest w ogóle co przewijać — przy wąskiej siatce szyny nie pokazujemy. */
  const scrollable = contentWidth > viewportWidth + 1;

  /**
   * Czy siatka jest odjechana od lewej krawędzi. Steruje krawędzią kolumny
   * godzin: w spoczynku sąsiaduje ona z obramowaniem pierwszego kortu, więc
   * własna kreska dałaby podwójną linię — pokazujemy ją dopiero wtedy, gdy
   * kolumny faktycznie zaczynają pod nią wjeżdżać.
   */
  const [scrolledX, setScrolledX] = useState(false);

  /** Przepisuje pozycję między szyną a siatką; równe wartości pomijamy, żeby nie zapętlić zdarzeń. */
  function syncScroll(from: HTMLDivElement | null, to: HTMLDivElement | null) {
    if (!from || !to || to.scrollLeft === from.scrollLeft) return;
    to.scrollLeft = from.scrollLeft;
  }

  /** Przewinięcie siatki — przesuwa szynę i odsłania krawędź kolumny godzin. */
  function handleBodyScroll() {
    syncScroll(bodyRef.current, railRef.current);
    setScrolledX((bodyRef.current?.scrollLeft ?? 0) > 0);
  }

  /** Przewinięcie szyny — to samo w drugą stronę. */
  function handleRailScroll() {
    syncScroll(railRef.current, bodyRef.current);
    setScrolledX((railRef.current?.scrollLeft ?? 0) > 0);
  }

  /** Klasy przyklejonej kolumny godzin — wspólne dla narożnika i skali. */
  const stickyColumn = cn(
    'bg-surface sticky left-0',
    scrolledX && 'border-border border-r shadow-[0.125rem_0_0.25rem_-0.125rem_rgba(0,0,0,0.18)]',
  );

  return (
    <div className="border-border overflow-hidden rounded-xl border">
      {scrollable && (
        <div
          ref={railRef}
          onScroll={handleRailScroll}
          aria-hidden="true"
          className="border-border overflow-x-auto overflow-y-hidden border-b"
        >
          <div style={{ width: `${contentWidth}px`, height: '1px' }} />
        </div>
      )}
      <div
        ref={bodyRef}
        onScroll={handleBodyScroll}
        className={cn(
          'overflow-x-auto',
          // Dolny pasek chowamy dopiero wtedy, gdy górny faktycznie stoi —
          // inaczej przy wąskim kalendarzu zabralibyśmy jedyny sposób przewijania.
          scrollable && '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        )}
      >
        <div
          className="grid min-w-max"
          style={{
            gridTemplateColumns: `${HOUR_COLUMN_REM}rem repeat(${courts.length}, minmax(${COURT_COLUMN_MIN_REM}rem, 1fr))`,
          }}
        >
        {/* Narożnik siatki — przykleja się razem z kolumną godzin, żeby przy
            przewijaniu w bok nagłówki kortów nie wjeżdżały pod podpis „Godzina". */}
        <div className={cn('border-border text-fg-muted z-30 border-b px-2 py-2 text-xs', stickyColumn)}>
          {t('infrastructure.calendar.hour')}
        </div>

        {/* Nagłówki kortów z akcjami */}
        {courts.map((court) => (
          <div
            key={court.id}
            className="border-border flex items-start justify-between gap-1 border-b border-l px-2 py-2"
          >
            <div className="min-w-0">
              <p className="text-fg-strong truncate text-[0.8125rem] font-bold" title={court.name}>
                {court.name}
              </p>
              <p className="text-fg-muted truncate text-[0.6875rem]">
                {[t('infrastructure.calendar.court', { number: court.number }), court.surface]
                  .filter(Boolean)
                  .join(' • ')}
              </p>
            </div>
            <div className="flex shrink-0 items-center">
              <button
                type="button"
                aria-label={t('infrastructure.calendar.editCourt')}
                title={t('infrastructure.calendar.editCourt')}
                onClick={() => onEditCourt(court)}
                className="text-fg-subtle hover:text-fg-strong p-1 transition-colors"
              >
                <Pencil aria-hidden="true" className="size-3.5" />
              </button>
              <button
                type="button"
                aria-label={t('infrastructure.calendar.deleteCourt')}
                title={t('infrastructure.calendar.deleteCourt')}
                onClick={() => onDeleteCourt(court)}
                className="text-fg-subtle hover:text-danger-600 p-1 transition-colors"
              >
                <Trash2 aria-hidden="true" className="size-3.5" />
              </button>
            </div>
          </div>
        ))}

        {/* Skala godzin. `sticky left-0` trzyma ją na oku przy przewijaniu w bok —
            bez godzin kolumny kortów są nieczytelne. Nieprzezroczyste tło i z-index
            ponad kartami meczów (z-auto) oraz cieniem upuszczania (z-10) sprawiają,
            że kolumny wjeżdżają pod spód, a nie przez nią. */}
        <div className={cn('z-20', stickyColumn)} style={{ height: `${bodyHeight}rem` }}>
          {hours.map((hour, index) => (
            <span
              key={hour}
              className="text-fg-subtle absolute right-2 -translate-y-1/2 text-[0.6875rem] tabular-nums"
              style={{ top: `${(index / spans) * 100}%` }}
            >
              {String(hour).padStart(2, '0')}:00
            </span>
          ))}
        </div>

        {/* Kolumny kortów */}
        {courts.map((court) => {
          const entries = matchesOnCourt(planner.scheduledMatches, court.id);
          const blocks = blocksOnDay(court, date);
          const ghost =
            hoverSlot && hoverSlot.courtId === court.id && draggingEntry
              ? slotBar(hoverSlot.time, draggingEntry.durationMinutes)
              : null;
          // Magnesy „na styk" z sąsiednimi meczami i przerwami tego kortu —
          // upuszczenie blisko krawędzi ląduje dokładnie na niej, bez luki.
          const magnets = draggingEntry
            ? magnetStarts(planner.scheduledMatches, court, date, {
                durationMinutes: draggingEntry.durationMinutes,
                matchId: draggingEntry.match.id,
              })
            : [];

          return (
            <div
              key={court.id}
              className="border-border relative border-l"
              style={{ height: `${bodyHeight}rem` }}
              onDragOver={(event: DragEvent<HTMLDivElement>) => {
                event.preventDefault();
                const rect = event.currentTarget.getBoundingClientRect();
                onHoverSlot({
                  courtId: court.id,
                  time: snapDropToTime(event.clientY - rect.top, rect.height, magnets),
                });
              }}
              onDragLeave={() => onHoverSlot(null)}
              onDrop={(event: DragEvent<HTMLDivElement>) => {
                event.preventDefault();
                const rect = event.currentTarget.getBoundingClientRect();
                const time = snapDropToTime(event.clientY - rect.top, rect.height, magnets);
                onHoverSlot(null);
                onDropOnCourt(court, event.dataTransfer.getData('text/plain'), time);
              }}
            >
              {/* Linie godzin pod blokami */}
              <div aria-hidden="true" className="absolute inset-0">
                {hours.slice(0, -1).map((hour, index) => (
                  <span
                    key={hour}
                    className="border-border/60 absolute inset-x-0 border-t"
                    style={{ top: `${(index / spans) * 100}%` }}
                  />
                ))}
              </div>

              {/* Przerwy techniczne — kreskowane, tylko do odczytu */}
              {blocks.map((block) => {
                const bar = slotBar(
                  block.startTime,
                  toMinutes(block.endTime) - toMinutes(block.startTime),
                );
                return (
                  <div
                    key={block.id}
                    title={`${t('infrastructure.legend.break')} ${hhmm(block.startTime)}–${hhmm(block.endTime)}${block.reason ? `: ${block.reason}` : ''}`}
                    className="border-border bg-surface-sunken absolute inset-x-1 overflow-hidden rounded-lg border bg-[repeating-linear-gradient(135deg,transparent,transparent_6px,var(--color-border)_6px,var(--color-border)_7px)] px-2 py-1"
                    style={{ top: `${bar.startPercent}%`, height: `${bar.sizePercent}%` }}
                  >
                    <span className="text-fg-subtle text-[0.625rem]">
                      {t('infrastructure.legend.break')}
                    </span>
                  </div>
                );
              })}

              {/* Mecze rozplanowane */}
              {entries.map((entry) => {
                const match = entry.match;
                if (!match.scheduledTime) return null;
                const bar = slotBar(match.scheduledTime, entry.durationMinutes);
                const endTime = fromMinutes(toMinutes(match.scheduledTime) + entry.durationMinutes);
                const color = colorOf(match.competitionId);
                const pendingLabel = t('infrastructure.calendar.pending');
                const home = sideLabel(match.team1Ids, names, pendingLabel);
                const away = sideLabel(match.team2Ids, names, pendingLabel);
                // Krótki mecz = niska karta. Zamiast przycinać nazwiska w połowie
                // liter, zbijamy układ do tylu linii, ile faktycznie się mieści.
                const density = matchCardDensity(slotHeightRem(entry.durationMinutes, HOUR_ROW_REM));
                const removeLabel = t('infrastructure.calendar.removeFromCalendar');
                // Strony meczu zawsze jedna pod drugą — w deblu linia to cała para,
                // więc kurczy się sama czcionka, nie liczba linii.
                const nameClass = cn(
                  'text-fg-strong truncate font-bold',
                  density === 'full'
                    ? 'text-xs leading-tight'
                    : density === 'compact'
                      ? 'text-[0.6875rem] leading-tight'
                      : 'text-[0.625rem] leading-none',
                );

                return (
                  <div
                    key={match.id}
                    draggable
                    onDragStart={(event) => {
                      event.dataTransfer.setData('text/plain', match.id);
                      event.dataTransfer.effectAllowed = 'move';
                      onDragStartMatch(match.id);
                    }}
                    onDragEnd={onDragEndMatch}
                    // Pełny opis zawsze zostaje w tytule — na zbitej karcie to jedyne
                    // miejsce, gdzie widać komplet danych meczu.
                    title={`${hhmm(match.scheduledTime)}–${endTime} • ${entry.competitionName ?? ''} • ${home} vs ${away}`}
                    className={cn(
                      'group absolute inset-x-1 flex cursor-grab flex-col overflow-hidden rounded-lg border px-2 active:cursor-grabbing',
                      density === 'full' ? 'py-1' : density === 'compact' ? 'py-0.5' : 'py-0',
                      color.card,
                    )}
                    style={{ top: `${bar.startPercent}%`, height: `${bar.sizePercent}%` }}
                  >
                    {/* Osobny wiersz z godzinami mieści się tylko na pełnej karcie —
                        niżej godzina wchodzi jako przedrostek pierwszej strony. */}
                    {density === 'full' && (
                      <span className="text-fg-muted flex items-center gap-1 text-[0.625rem] leading-tight tabular-nums">
                        {hhmm(match.scheduledTime)} – {endTime}
                        {match.matchNumber !== null && (
                          <span className="truncate">
                            • {t('infrastructure.pool.matchNumber', { number: match.matchNumber })}
                          </span>
                        )}
                        <RemoveSlotButton
                          label={removeLabel}
                          onClick={() => onClearSlot(match.id)}
                          className="ml-auto"
                        />
                      </span>
                    )}

                    <span className={nameClass}>
                      {density !== 'full' && (
                        <span className="text-fg-muted mr-1 font-normal tabular-nums">
                          {hhmm(match.scheduledTime)}
                        </span>
                      )}
                      {home}
                    </span>
                    <span className={nameClass}>{away}</span>

                    {/* Bez wiersza z godzinami krzyżyk nie ma gdzie stać w treści —
                        kładziemy go nad nią, w rogu karty. */}
                    {density !== 'full' && (
                      <RemoveSlotButton
                        label={removeLabel}
                        onClick={() => onClearSlot(match.id)}
                        className="bg-surface/80 absolute top-0 right-1 rounded"
                      />
                    )}
                  </div>
                );
              })}

              {/* Cień docelowego slotu podczas przeciągania. Na czerwono, gdy
                  slotu nie da się zająć — kort zajęty, przerwa techniczna albo
                  zawodnik grający w tym czasie inny mecz (także w innym turnieju). */}
              {ghost && hoverSlot && (
                <div
                  aria-hidden="true"
                  className={cn(
                    'pointer-events-none absolute inset-x-1 z-10 flex flex-col items-center rounded-lg border-2 border-dashed',
                    hoverConflict
                      ? 'border-danger-500 bg-danger-50/80'
                      : 'border-brand-500 bg-brand-subtle/70',
                  )}
                  style={{ top: `${ghost.startPercent}%`, height: `${ghost.sizePercent}%` }}
                >
                  {/* Godzina musi być czytelna jeszcze przed upuszczeniem, dlatego
                      wychodzi ponad cień jako pełny znacznik, a nie drobny podpis. */}
                  <span
                    className={cn(
                      'ring-surface -translate-y-1/2 rounded-md px-2 py-0.5 text-sm leading-tight font-bold tabular-nums shadow-md ring-2',
                      hoverConflict ? 'bg-danger-500 text-white' : 'bg-brand text-fg-on-brand',
                    )}
                  >
                    {hoverSlot.time}
                  </span>
                  {/* Sam kolor nie mówi, CO blokuje slot — powód dopisujemy pod godziną. */}
                  {hoverConflictNote && (
                    <span className="text-danger-600 max-w-full truncate px-1 text-[0.625rem] font-bold">
                      {hoverConflictNote}
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })}
        </div>
      </div>
    </div>
  );
}

/** Krzyżyk zdejmujący mecz z kalendarza — pokazywany po najechaniu na kartę. */
function RemoveSlotButton({
  label,
  onClick,
  className,
}: {
  label: string;
  onClick: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className={cn(
        'text-fg-subtle hover:text-danger-600 hidden shrink-0 transition-colors group-hover:block',
        className,
      )}
    >
      <X aria-hidden="true" className="size-3.5" />
    </button>
  );
}

/** Etykieta strony meczu — nazwiska albo „Do ustalenia" dla pustego slotu drabinki. */
function sideLabel(
  ids: readonly string[],
  names: ReadonlyMap<string, string>,
  pendingLabel: string,
): string {
  if (ids.length === 0) return pendingLabel;
  return ids.map((id) => names.get(id) ?? '…').join(' / ');
}
